import { useState, InputHTMLAttributes } from 'react';

interface PasswordInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  showStrength?: boolean;
  label?: string;
  error?: string;
}

interface StrengthResult {
  score: number;
  label: string;
  barClass: string;
  textClass: string;
}

function getPasswordStrength(password: string): StrengthResult {
  let score = 0;

  if (password.length >= 12) score++;
  if (password.length >= 16) score++;
  if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score++;
  if (/[0-9]/.test(password) && /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) score++;

  const labels = ['Very Weak', 'Weak', 'Fair', 'Strong', 'Very Strong'];
  const barClasses = ['bg-danger', 'bg-warning', 'bg-primary', 'bg-primary', 'bg-success'];
  const textClasses = ['text-danger', 'text-warning', 'text-primary', 'text-primary', 'text-success'];

  return {
    score,
    label: labels[score] ?? 'Unknown',
    barClass: barClasses[score] ?? 'bg-border',
    textClass: textClasses[score] ?? 'text-text-muted',
  };
}

export default function PasswordInput({
  showStrength = false,
  label,
  error,
  className = '',
  onChange,
  value,
  defaultValue,
  ...props
}: PasswordInputProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [uncontrolledValue, setUncontrolledValue] = useState(String(defaultValue ?? ''));

  const isControlled = value !== undefined;
  const currentValue = isControlled ? String(value ?? '') : uncontrolledValue;
  const strength = showStrength && currentValue ? getPasswordStrength(currentValue) : null;

  const errorClasses = error
    ? 'border-danger focus:border-danger focus:ring-danger'
    : 'border-border focus:border-primary focus:ring-primary';

  const handleChange: InputHTMLAttributes<HTMLInputElement>['onChange'] = (e) => {
    onChange?.(e);
    if (!isControlled) {
      setUncontrolledValue(e.currentTarget.value);
    }
  };

  return (
    <div className="w-full">
      {label && <label className="block text-sm font-medium text-text-muted mb-2">{label}</label>}

      <div className="relative">
        <input
          type={showPassword ? 'text' : 'password'}
          className={`input-field pr-10 ${errorClasses} ${className}`}
          onChange={handleChange}
          value={isControlled ? currentValue : undefined}
          defaultValue={!isControlled ? (defaultValue as string | number | readonly string[] | undefined) : undefined}
          {...props}
        />

        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute inset-y-0 right-0 pr-3 flex items-center text-text-muted hover:text-text-header transition-colors"
          aria-label={showPassword ? 'Hide password' : 'Show password'}
        >
          {showPassword ? (
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
              />
            </svg>
          ) : (
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
              />
            </svg>
          )}
        </button>
      </div>

      {strength && (
        <div className="mt-3">
          <div className="flex gap-1">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className={`h-1.5 flex-1 rounded ${i <= strength.score ? strength.barClass : 'bg-border'}`} />
            ))}
          </div>
          <p className={`text-xs mt-2 ${strength.textClass}`}>{strength.label}</p>
        </div>
      )}

      {error && <p className="mt-2 text-sm text-danger">{error}</p>}
    </div>
  );
}
